// Feature/service-request intake client (typed help/intake framework, commit 4).
//
// Sibling of supportTicketApi.js, sharing its idempotency-key helper rather than
// re-inventing one. Two server contracts this module exists to honor:
//
//   - Create and the ANALYSIS generation require a UUID Idempotency-Key. A retry
//     after an ambiguous failure must reuse the SAME key so the server replays the
//     original result instead of creating a second intake or drafting a second
//     analysis over the reporter's edits.
//   - Every aggregate mutation carries `expectedRevision`, and a stale one comes
//     back as HTTP 409 with `error.details.currentRevision`. Callers must keep the
//     reporter's typed work and refresh, never discard it.
//
// Nothing here decides whether an analysis is AI-authored or deterministic — the
// server does, and the page renders the label the server returned.

import { api } from '../../lib/apiClient.js';

import { newIdempotencyKey } from './supportTicketApi.js';

const BASE = '/feedback/intakes';

export { newIdempotencyKey };

function intakePath(intakeId, suffix = '') {
  return `${BASE}/${encodeURIComponent(intakeId)}${suffix}`;
}

export async function createRequestIntake(payload, { idempotencyKey } = {}) {
  return api.post(
    BASE,
    { intakeType: 'feature_service_request', ...payload },
    { headers: { 'Idempotency-Key': idempotencyKey || newIdempotencyKey() } },
  );
}

export async function getRequestIntake(intakeId) {
  return api.get(intakePath(intakeId));
}

export async function patchRequestIntake(intakeId, payload) {
  return api.patch(intakePath(intakeId), payload);
}

// Transcript coverage, the next deterministic question, and readiness. A GET, so it
// never spends a model call.
export async function getRequestTriageState(intakeId) {
  return api.get(intakePath(intakeId, '/triage'));
}

export async function appendRequestMessage(intakeId, expectedRevision, content) {
  return api.post(intakePath(intakeId, '/messages'), { expectedRevision, content });
}

// Ask PRIME for the next question. The response says whether a model authored it or
// the deterministic guide did; the page must render that distinction, never assume.
//
// A UUID Idempotency-Key is REQUIRED: this endpoint may invoke a model, so a retry
// after an ambiguous failure must replay the turn rather than spend a second model call
// and append a second question.
export async function requestNextProbe(intakeId, expectedRevision, { idempotencyKey } = {}) {
  return api.post(
    intakePath(intakeId, '/probe'),
    { expectedRevision },
    { headers: { 'Idempotency-Key': idempotencyKey || newIdempotencyKey() } },
  );
}

// `mode: 'deterministic'` is the path that needs no model at all — used when the
// reporter chooses to write the analysis themselves, and when generation failed.
export async function generateRequestAnalysis(
  intakeId,
  expectedRevision,
  { mode = 'ai', idempotencyKey, signal } = {},
) {
  return api.post(
    intakePath(intakeId, '/analysis'),
    { expectedRevision, mode },
    {
      headers: { 'Idempotency-Key': idempotencyKey || newIdempotencyKey() },
      // The reporter can stop waiting for a slow generation; the request is genuinely
      // aborted rather than left running with its result thrown away.
      ...(signal ? { signal } : {}),
    },
  );
}

export async function approveRequestAnalysis(intakeId, expectedRevision) {
  return api.post(intakePath(intakeId, '/analysis/approve'), { expectedRevision });
}

export async function prepareRequestIntake(intakeId, expectedRevision) {
  return api.post(intakePath(intakeId, '/prepare'), { expectedRevision });
}

export async function submitRequestIntake(intakeId, expectedRevision, { idempotencyKey } = {}) {
  return api.post(
    intakePath(intakeId, '/submit'),
    { expectedRevision },
    { headers: { 'Idempotency-Key': idempotencyKey || newIdempotencyKey() } },
  );
}

export async function cancelRequestIntake(intakeId, expectedRevision, reason = null) {
  return api.post(intakePath(intakeId, '/cancel'), { expectedRevision, reason });
}
